import React, { useState } from "react";
import TrainUploadStage from "../components/train/TrainUploadStage";
import dynamic from "next/dynamic";
import { Spinner } from "@jammable/ui-components/Loader";
import { useFormik } from "formik";
import CustomModelDemo from "../components/CustomModelDemo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@jammable/ui-components/Accordion";
import connectToDb from "../utils/mongodb";
import { useAuth } from "../context/AuthProvider";
import useSubscription from "../hooks/useSubscription";

import * as yup from "yup";
import { NextSeo } from "next-seo";
import { asLocale, intlLocale, useT } from "@jammable/ui-core/src/i18n";
import { localeSeo } from "@/utils/localeSeo";
import { useRouter } from "next/router";
import { MIN_TRAINING_SECONDS } from "@/lib/train/clips";
import { trainingEta } from "@/lib/train/eta";
import { etaLabel } from "../components/train/TrainSummaryCard";

// Step 2 (name, image, categories, the Creator upsell) shows only after the
// upload step, so it loads then instead of with the page.
const TrainInfoStage = dynamic(
  () => import("../components/train/TrainInfoStage"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);


const TrainVoicePage = ({ totalVoices }) => {
  const { locale } = useRouter();
  const t = useT();
  const [localImage, setLocalImage] = useState(null);

  const [settings, setSettings] = useState({
    removeReverb: false,
    removeInstrumentals: false,
    isPublic: true,
  });
  const [stage, setStage] = useState(0);
  const [files, setFiles] = useState([]);
  const [scannedFiles, setScannedFiles] = useState([]);
  // Per file: its silence and what the person cut (lib/train/clips).
  const [clips, setClips] = useState({});

  const [image, setImage] = useState(null);

  // Same rule TrainInfoStage uses to gate the Start button: Creator and Power
  // plans train without passes, everyone else spends users.customModels.
  const { user } = useAuth();
  const { hasCreatorPlan, hasPowerPlan } = useSubscription();
  const passesLabel =
    hasCreatorPlan() || hasPowerPlan()
      ? "Unlimited"
      : (user?.customModels ?? 0);

  const validationSchema = yup.object().shape({
    name: yup.string().trim().required("YouTube link is required").max(40),
    description: yup.string().trim().max(200),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
    },
    validationSchema,
  });

  const seo = localeSeo(
    asLocale(locale),
    "https://www.jammable.com/train",
  );
  return (
    <div className="tw-bg-white tw-text-jam-ink">
      <NextSeo
        title={t("train.seo.title")}
        description={t("train.seo.description")}
        canonical={seo.canonical}
        languageAlternates={seo.languageAlternates}
      />
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-6 tw-px-4 tw-pb-28 tw-pt-2 md:tw-px-8 md:tw-pt-4">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <h1 className="jam-display tw-m-0 tw-text-[36px] tw-font-extrabold tw-leading-none md:tw-text-[52px]">
            {t("train.h1")}
          </h1>
          <div className="tw-text-[15px] tw-font-bold tw-text-jam-muted">
            {t("train.sub")}
          </div>
          {totalVoices > 0 && (
            <span className="tw-mt-2 tw-self-start tw-rounded-full tw-bg-jam-lav tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold">
              {t("train.voicesTrained", {
                count: new Intl.NumberFormat(
                  intlLocale(asLocale(locale)),
                ).format(totalVoices),
              })}
            </span>
          )}
        </div>

        {stage === 0 && (
          <TrainUploadStage
            setStage={setStage}
            files={files}
            setFiles={setFiles}
            scannedFiles={scannedFiles}
            setScannedFiles={setScannedFiles}
            clips={clips}
            setClips={setClips}
            passesLabel={passesLabel}
          />
        )}
        {stage === 0 && <CustomModelDemo />}
        {stage === 1 && (
          <TrainInfoStage
            settings={settings}
            setSettings={setSettings}
            setStage={setStage}
            files={files}
            setFiles={setFiles}
            image={image}
            setImage={setImage}
            localImage={localImage}
            formik={formik}
            setLocalImage={setLocalImage}
            scannedFiles={scannedFiles}
            clips={clips}
          />
        )}

        <div className="tw-mt-4 tw-grid tw-items-start tw-gap-5">
          <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3">
            <h2 className="jam-display tw-m-0 tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
              {t("train.faq.title")}
            </h2>
            <Accordion
              type="single"
              collapsible
              className="tw-w-full tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-px-5 tw-py-2"
            >
              <AccordionItem value="item-1">
                <AccordionTrigger className="tw-font-extrabold">
                  {t("train.faq.dataset.q")}
                </AccordionTrigger>
                <AccordionContent className="tw-font-semibold tw-text-jam-muted">
                  <ul
                    style={{
                      listStyleType: "initial",
                      marginBottom: 0,
                    }}
                  >
                    <li>{t("train.faq.dataset.a1")}</li>
                    <li>
                      {t("train.faq.dataset.a2", {
                        min: Math.round(MIN_TRAINING_SECONDS / 60),
                      })}
                    </li>
                    <li>{t("train.faq.dataset.a3")}</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger className="tw-font-extrabold">
                  {t("train.faq.private.q")}
                </AccordionTrigger>
                <AccordionContent className="tw-font-semibold tw-text-jam-muted">
                  {t("train.faq.private.a")}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger className="tw-font-extrabold">
                  {t("train.faq.time.q")}
                </AccordionTrigger>
                <AccordionContent className="tw-font-semibold tw-text-jam-muted">
                  {t("train.faq.time.a", {
                    short: etaLabel(t, trainingEta(10 * 60).typicalMin),
                    long: etaLabel(t, trainingEta(60 * 60).typicalMin),
                  })}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainVoicePage;

export const getServerSideProps = async ({ req }) => {
  const Model = (await import("../models/Model")).default;
  await connectToDb();

  const count = await Model.estimatedDocumentCount();

  const DEFAULT_PROPS = {
    totalVoices: count,
  };

  try {
    return {
      props: {
        totalVoices: DEFAULT_PROPS.totalVoices,
      },
    };
  } catch (error) {
    console.log(error);

    return {
      props: DEFAULT_PROPS,
    };
  }
};
